import React, { useState, useCallback, useEffect } from 'react';

import { useDataLayerActions } from '../../hooks';

export function ContentModalKlaviyoForm({
  klaviyoForm,
  linkText = '',
  email = '',
  setEmail = () => {},
  responseStatus,
  setResponseStatus,
}) {
  const { sendSubscribeEvent } = useDataLayerActions();

  const [properties, setProperties] = useState(() => {
    if (klaviyoForm?.properties) {
      return klaviyoForm.properties.reduce((acc, prop) => {
        acc[prop.name] = prop.value || '';
        return acc;
      }, {});
    }
    return {};
  });
  const [statusMessage, setStatusMessage] = useState('');

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handlePropertiesChange = useCallback((e) => {
    const { name, value } = e.target;
    setProperties((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Sync properties with linkText for passLinkTitle fields
  useEffect(() => {
    if (klaviyoForm?.properties) {
      setProperties((prev) => {
        const updated = { ...prev };
        klaviyoForm.properties.forEach((prop) => {
          if (prop.passLinkTitle) {
            updated[prop.name] = linkText;
          }
        });
        return updated;
      });
    }
  }, [linkText, klaviyoForm]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Filter out empty properties
      const filteredProperties = Object.fromEntries(
        Object.entries(properties).filter(
          ([_, value]) => value !== '' && value !== undefined && value !== null
        )
      );

      try {
        const endpoint = '/api/klaviyo/customEmailForm';
        const options = {
          method: 'POST',
          headers: {
            revision: '2025-04-15',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'subscribeToEmail',
            list: klaviyoForm?.listId,
            email,
            properties: filteredProperties,
          }),
        };

        const response = await fetch(endpoint, options);
        if (response.ok && response.status === 202) {
          setStatusMessage('Thank you for subscribing!');
          sendSubscribeEvent({ email });
          setResponseStatus('success');
        } else {
          const errorData = await response.json();
          let errorMsg = 'Failed to subscribe. Please try again later.';
          if (errorData && errorData.errors && errorData.errors[0]) {
            const { title, detail } = errorData.errors[0];
            errorMsg = `${title}${detail ? ': ' + detail : ''}`;
          }
          setStatusMessage(errorMsg);
          setResponseStatus('error');
        }
      } catch (error) {
        console.error('Subscription error:', error.message);
        setStatusMessage('Failed to subscribe. Please try again later.');
        setResponseStatus('error');
      }
    },
    [sendSubscribeEvent, email, properties, klaviyoForm]
  );

  return (
    <>
      {klaviyoForm?.title && (
        <h3 className="text-lg font-bold text-center">
          {klaviyoForm.title}
        </h3>
      )}
      {klaviyoForm?.text && (
        <p className="text-center font-medium mt-2.5">
          {klaviyoForm.text}
        </p>
      )}
      <form className="flex flex-col gap-1 pt-2.5" onSubmit={handleSubmit}>
        <label htmlFor="newsletterEmail">
          Email:
        </label>
        <input
          id="gameDayNewsletterEmail"
          className="input-text text-text"
          name="gameDayNewsletterEmail"
          autoComplete="email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={handleEmailChange}
          required
          aria-required="true"
          aria-label="Email address for subscription"
        />

        {klaviyoForm?.properties?.map((property, index) => (
          <React.Fragment key={property?.name || index}>
            <label className="sr-only" htmlFor={`property_${property.name}`}>
              {property.name}:
            </label>
            <input
              id={`property_${property.name}`}
              className={`input-text text-text ${
                property.hidden ? 'sr-only' : ''
              }`}
              name={property.name}
              type={property.type || 'text'}
              placeholder={property.placeholder || `Enter your ${property.name}`}
              value={
                property.passLinkTitle
                  ? linkText
                  : properties[property.name] || ''
              }
              onChange={handlePropertiesChange}
              readOnly={property.passLinkTitle}
              hidden={property.hide}
              aria-label={property.name}
            />
          </React.Fragment>
        ))}

        <button
          aria-label="Subscribe to notifications"
          className="btn-primary mt-1.5 w-full"
          type="submit"
        >
          Subscribe
        </button>

        <p className="text-xs text-center mt-2.5">
          By submitting your email address, you agree to receive marketing emails from Rhoback. We may use information collected about you on our site to suggest other products and offers. You can unsubscribe at any time.
        </p>

        {statusMessage && (
          <p
            className={`mt-2.5 px-4 py-2 text-base transition-all md:text-sm ${
              responseStatus === 'error'
                ? 'font-semibold border border-red-300 bg-red-100 text-red-800'
                : 'font-semibold border border-green-200 bg-green-50 text-current text-green-800'
            }`}
            role={responseStatus === 'error' ? 'alert' : undefined}
          >
            {statusMessage}
          </p>
        )}
      </form>    
    </>

  );
}

ContentModalKlaviyoForm.displayName = 'ContentModalKlaviyoForm';
